import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'ncoded-component-library';
import useQueryParams from 'hooks/useQueryParams';
import { getIsDesc, getSortBy, getSortValue } from 'utils';
import { SortByForm, SORTING_ORDER } from 'models/Sort';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { MULTI_SORT_SEPARATOR } from 'data/global';
import MultiSortOptions, {
  INITIAL_VALUE,
  MultiSortOptionsProps,
} from './components/MultiSortOptions/MultiSortOptions.component';

import './MultiSortModal.styles.scss';

type MultiSortModalProps = {
  className?: string;
  title?: string;
} & MultiSortOptionsProps &
  ModalProps;

const MultiSortModal: React.ForwardRefRenderFunction<
  ModalRef,
  MultiSortModalProps
> = (props, ref) => {
  const { t } = useTranslation();

  const {
    className,
    sortDirectionTranslations,
    title = t('sort'),
    options = [],
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const {
    params: { sort },
    setQueryParam,
  } = useQueryParams<{ sort: string; page: string }>();

  const classes = classNames('multi-sort-modal', className);

  const initialValues = useMemo(
    () => ({
      sortBy: sort
        ? sort.split(MULTI_SORT_SEPARATOR).map((el) => ({
            name: getSortBy(el),
            order: getIsDesc(el) ? SORTING_ORDER.Z_TO_A : SORTING_ORDER.A_TO_Z,
          }))
        : [INITIAL_VALUE],
    }),
    [sort],
  );

  const onSubmit = useCallback(
    ({ sortBy }: SortByForm) => {
      const newSort = sortBy
        .filter(({ name }) => name)
        .map(({ name, order }) =>
          getSortValue(name, order === SORTING_ORDER.Z_TO_A),
        )
        .join(MULTI_SORT_SEPARATOR);

      setQueryParam('sort', newSort);
      modal?.close();
    },
    [modal, setQueryParam],
  );

  if (!options.length) return null;

  return (
    <Modal ref={modalRef} className={classes} title={title} {...rest}>
      <Form
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <MultiSortOptions
              options={options}
              sortDirectionTranslations={sortDirectionTranslations}
            />

            <div className="actions">
              <Button variant="link" type="button" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={invalid}>
                {t('sort')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(MultiSortModal);
